import { APPLE_PAY_VERSION, MERCHANT_NAME } from '@/constants/applePay';
import { reportError } from '@/services/error-reporting-service';
import { styled } from '@mui/material';
import { ApplePay, applePay, ApplePayPayload, ApplePayPaymentRequest, ApplePaySession, Client } from 'braintree-web';
import { useEffect, useRef } from 'react';
interface BraintreeApplePayProps {
  onSuccess: (nonce: string) => Promise<void>;
  clientInstance: Client;
  legal: string;
  amount: string;
}

declare global {
  interface Window {
    ApplePaySession?: ApplePaySession;
  }
}

export function BraintreeApplePay({ onSuccess, clientInstance, amount }: BraintreeApplePayProps) {
  const applePayInstance = useRef<ApplePay | null>(null);

  // Initialize Apple Pay with the Braintree client instance
  useEffect(() => {
    async function createApplePaymentInstance() {
      try {
        if (!applePayInstance.current) {
          const applePaymentInstance = await applePay.create({
            client: clientInstance,
          });
          applePayInstance.current = applePaymentInstance;
        }
      } catch (error) {
        reportError({ error });
      }
    }
    void createApplePaymentInstance();
  }, [clientInstance]);

  // Create Apple Pay token on submit
  function createApplePayToken(): void {
    if (!applePayInstance.current) {
      reportError({ error: 'Apple Pay instance failed to load.' });
      return;
    }
    const paymentRequest: ApplePayPaymentRequest = applePayInstance.current.createPaymentRequest({
      total: {
        label: MERCHANT_NAME,
        amount,
      },
    });

    if (window.ApplePaySession !== undefined) {
      const session = new ApplePaySession(APPLE_PAY_VERSION, paymentRequest);
      session.onvalidatemerchant = (event: { validationURL: string }) => {
        if (!applePayInstance.current) {
          return;
        }
        applePayInstance.current
          .performValidation({
            validationURL: event.validationURL,
            displayName: MERCHANT_NAME,
          })
          .then((merchantSession) => {
            session.completeMerchantValidation(merchantSession);
          })
          .catch((error) => {
            reportError({ error });
            session.abort();
          });
      };

      session.onpaymentauthorized = (event: { payment: { token: string } }) => {
        if (!applePayInstance.current) {
          return;
        }
        applePayInstance.current
          .tokenize({
            token: event.payment.token,
          })
          .then((payload: ApplePayPayload) => {
            if (payload.nonce) {
              session.completePayment(0);
              return onSuccess(payload.nonce);
            }
          })
          .catch((error) => {
            session.completePayment(1);
            reportError({ error });
          });
      };

      session.oncancel = () => {
        return;
      };

      try {
        session.begin();
      } catch (error) {
        reportError({ error });
        session.abort();
      }
    }
  }

  return (
    <ApplePayButton
      type='button'
      onClick={() => {
        void createApplePayToken();
      }}
    />
  );
}

const ApplePayButton = styled('button')`
  height: 42px;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-style: black;
`;
